import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ForgotPasswordMethod,
  IOTPState,
  IParsedOTPData,
} from 'src/shared/interface/forgot-password';
import { FORGOT_PASSWORD_STEP } from 'src/shared/constants';

const createInitialOTPState = (): IOTPState => ({
  value: '',
  token: '',
  expiresAt: null,
  resendCooldownUntil: null,
  isResendLocked: false,
  resendLockUntil: null,
  isVerifyLocked: false,
  verifyLockUntil: null,
  resendCount: 0,
  maxResendCount: 5,
  verifyAttempts: 0,
  maxVerifyAttempts: 5,
});

export interface ForgotPasswordState {
  currentStep: (typeof FORGOT_PASSWORD_STEP)[keyof typeof FORGOT_PASSWORD_STEP];
  method: ForgotPasswordMethod;
  username: string;
  email: string;
  phoneNumber: string;
  otp: IOTPState;
  lastUpdated: number;
  preservedResendData: {
    resendCount: number;
    maxResendCount: number;
  } | null;
}

const initialState: ForgotPasswordState = {
  currentStep: FORGOT_PASSWORD_STEP.METHOD_SELECTION,
  method: ForgotPasswordMethod.EMAIL,
  username: '',
  email: '',
  phoneNumber: '',
  otp: createInitialOTPState(),
  lastUpdated: Date.now(),
  preservedResendData: null,
};

export const forgotPasswordSlice = createSlice({
  name: 'forgotPassword',
  initialState,
  reducers: {
    setMethod(state, action: PayloadAction<ForgotPasswordMethod>) {
      state.method = action.payload;
      state.lastUpdated = Date.now();
    },
    verifySuccess(
      state,
      action: PayloadAction<{
        username: string;
        identifier: string;
        otpData: IParsedOTPData;
      }>,
    ) {
      const { username, identifier, otpData } = action.payload;
      const isEmail = state.method === ForgotPasswordMethod.EMAIL;

      state.currentStep = FORGOT_PASSWORD_STEP.OTP_INPUT;
      state.username = username;
      if (isEmail) {
        state.email = identifier;
      } else {
        state.phoneNumber = identifier;
      }
      const resendCount = state.preservedResendData?.resendCount ?? otpData.resendCount ?? 0;
      const maxResendCount =
        state.preservedResendData?.maxResendCount ?? otpData.maxResendCount ?? 5;

      state.otp = {
        ...createInitialOTPState(),
        ...otpData,
        value: '',
        resendCount,
        maxResendCount,
      };

      state.preservedResendData = null;
      state.lastUpdated = Date.now();
    },
    otpSuccess(state, action: PayloadAction<{ otp: string; token: string }>) {
      state.currentStep = FORGOT_PASSWORD_STEP.NEW_PASSWORD;
      state.otp.value = action.payload.otp;
      state.otp.token = action.payload.token;
      state.lastUpdated = Date.now();
    },
    resetPasswordSuccess(state) {
      state.currentStep = FORGOT_PASSWORD_STEP.SUCCESS;
      state.lastUpdated = Date.now();
    },
    goBack(state) {
      switch (state.currentStep) {
        case FORGOT_PASSWORD_STEP.OTP_INPUT:
          state.currentStep = FORGOT_PASSWORD_STEP.METHOD_SELECTION;
          state.preservedResendData = {
            resendCount: state.otp.resendCount,
            maxResendCount: state.otp.maxResendCount,
          };
          state.otp = createInitialOTPState();
          break;
        case FORGOT_PASSWORD_STEP.NEW_PASSWORD:
          break;
        default:
          break;
      }
    },
    updateOTP(state, action: PayloadAction<Partial<IOTPState>>) {
      state.otp = { ...state.otp, ...action.payload };
      state.lastUpdated = Date.now();
    },
    reset() {
      return initialState;
    },
  },
});

export const {
  setMethod,
  verifySuccess,
  otpSuccess,
  resetPasswordSuccess,
  goBack,
  updateOTP,
  reset,
} = forgotPasswordSlice.actions;

export const forgotPasswordReducer = forgotPasswordSlice.reducer;
